import { mergeRefs } from "@react-aria/utils";
import React from "react";
import { ButtonContext, Group, InputContext, LabelContext, OverlayTriggerStateContext, PopoverContext, Provider, SelectContext, useSlottedContext } from "react-aria-components";
import { useFieldErrorContext } from "../../context.js";
import { Spinner } from "../Spinner.js";
import { useSuccessSpinnerState } from "../internal/useSuccessSpinnerState.js";
import { AvatarContext } from "./AvatarContext.js";
import ErrorIcon from "./ErrorIcon.js";
import * as styles from "./InputBuilder.css.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const BaseInputBuilder = ({
  slot,
  left,
  children,
  right,
  size = "m",
  loadingState,
  leadingText,
  isDisabled: isDisabledProp
}, externalRef) => {
  const triggerRef = React.useRef(null);
  const existingInput = useSlottedContext(InputContext, slot);
  const existingSelect = useSlottedContext(SelectContext, slot);
  const existingButton = useSlottedContext(ButtonContext, slot);
  const existingPopover = useSlottedContext(PopoverContext, slot);
  const validation = useFieldErrorContext();

  // Only used for getting the label id. We don't need to pass it in the Provider as we don't need to modify it.
  const existingLabel = useSlottedContext(LabelContext, slot);
  const {
    renderState,
    startAnimation
  } = useSuccessSpinnerState(loadingState);
  const isError = validation?.isInvalid === true || loadingState === "error";
  const isOpen = React.useContext(OverlayTriggerStateContext)?.isOpen === true;
  const rightContent = React.useMemo(() => {
    if (typeof right === "function") {
      return right({
        isError,
        isOpen,
        loadingState,
        loadingRenderState: renderState,
        loadingStartAnimation: startAnimation
      });
    }
    return right;
  }, [isError, isOpen, loadingState, renderState, right, startAnimation]);
  const isDisabled = isDisabledProp ?? existingInput?.disabled ?? undefined;
  return /*#__PURE__*/_jsxs(Group, {
    ref: mergeRefs(externalRef, triggerRef),
    "aria-labelledby": existingLabel?.id,
    className: styles.container({
      size,
      isError
    }),
    isDisabled: isDisabled,
    children: [/*#__PURE__*/_jsx(Provider, {
      values: [[AvatarContext, {
        size: "xxs",
        isInteractive: false,
        tooltip: false,
        insideInputBuilder: true
      }]],
      children: typeof left === "function" ? /*#__PURE__*/React.createElement(left, {
        className: styles.leftIcon
      }) : left
    }), leadingText != null && /*#__PURE__*/_jsx("span", {
      "aria-hidden": true,
      className: styles.leadingText({
        size
      }),
      children: leadingText
    }), /*#__PURE__*/_jsx("div", {
      role: "presentation",
      className: styles.inputWrapper,
      children: /*#__PURE__*/_jsx(Provider, {
        values: [[InputContext, {
          ...existingInput,
          className: styles.input({
            size
          })
        }], [SelectContext, {
          ...existingSelect,
          className: styles.select
        }], [ButtonContext, {
          ...existingButton,
          className: styles.input({
            size
          })
        }], [PopoverContext, {
          ...existingPopover,
          triggerRef
        }]],
        children: children
      })
    }), /*#__PURE__*/_jsxs(Group, {
      role: "presentation",
      className: styles.buttonGroup,
      children: [loadingState != null && typeof right !== "function" && /*#__PURE__*/_jsx(Spinner, {
        state: loadingState,
        renderState: renderState,
        startAnimation: startAnimation,
        size: "s",
        color: "royalBlue"
      }), isError && loadingState !== "error" && typeof right !== "function" && /*#__PURE__*/_jsx(ErrorIcon, {}), rightContent]
    })]
  });
};
export const InputBuilder = /*#__PURE__*/React.forwardRef(BaseInputBuilder);
//# sourceMappingURL=InputBuilder.js.map